import { useState, useCallback, useMemo, useRef } from "react";
import { KeyMasterAction } from "../services/KeyMasterService";
import { KeyMasterDataType, Realm, RealmLower, NotifyFn, KEYMASTER_LABELS, NAV } from "../constants";
import { HashParams, parseRealm, toErrorMessage } from "../utils";
import { useMountedRef } from "./useMountedRef";

export interface UseKeyMasterProps {
  onNotify: NotifyFn;
  convertFn: (value: string, dataType: KeyMasterDataType, action: KeyMasterAction, realm: RealmLower) => Promise<string>;
}

export interface UseKeyMasterReturn {
  realm: Realm;
  setRealm: (r: Realm) => void;
  action: KeyMasterAction;
  setAction: (a: KeyMasterAction) => void;
  dataType: KeyMasterDataType;
  setDataType: (d: KeyMasterDataType) => void;
  inputValue: string;
  setInputValue: (v: string) => void;
  outputValue: string;
  loading: boolean;
  realmLower: RealmLower;
  handleConvert: () => Promise<void>;
  handleSwap: () => void;
  inputLabel: string;
  outputLabel: string;
  selectedAction: { value: string; label: string };
}

const parseUrlState = (): { realm: Realm; dataType?: KeyMasterDataType } => {
  const params = HashParams.parse();
  return {
    realm: parseRealm(params.get("realm")),
    dataType: (params.get("dataType") as KeyMasterDataType) || undefined,
  };
};

const updateUrlState = (realm: Realm, dataType: KeyMasterDataType): void => {
  HashParams.update(NAV.ROUTES.KEYMASTER, {
    realm: realm.toLowerCase(),
    dataType,
  });
};

export function useKeyMaster({ onNotify, convertFn }: UseKeyMasterProps): UseKeyMasterReturn {
  const urlState = useMemo(parseUrlState, []);
  const [realm, setRealmState] = useState<Realm>(urlState.realm);
  const [action, setActionState] = useState<KeyMasterAction>("ENCRYPT");
  const [dataType, setDataTypeState] = useState<KeyMasterDataType>(urlState.dataType ?? KeyMasterDataType.FULL_NAME);
  const [inputValue, setInputValue] = useState("");
  const [outputValue, setOutputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const inFlightRef = useRef(false);
  const mountedRef = useMountedRef();

  const realmLower = useMemo(() => realm.toLowerCase() as RealmLower, [realm]);

  const setRealm = useCallback(
    (r: Realm) => {
      setRealmState(r);
      updateUrlState(r, dataType);
    },
    [dataType]
  );

  const setDataType = useCallback(
    (d: KeyMasterDataType) => {
      setDataTypeState(d);
      updateUrlState(realm, d);
    },
    [realm]
  );

  const setAction = useCallback(
    (next: KeyMasterAction): void => {
      if (next === action) {
        return;
      }
      setActionState(next);
      setOutputValue("");
    },
    [action]
  );

  const handleConvert = useCallback(async (): Promise<void> => {
    if (inFlightRef.current || !inputValue.trim()) {
      return;
    }

    inFlightRef.current = true;
    setLoading(true);
    try {
      const result = await convertFn(inputValue, dataType, action, realmLower);
      if (mountedRef.current) {
        setOutputValue(result);
      }
      onNotify("success", action === "ENCRYPT" ? KEYMASTER_LABELS.SUCCESS_ENCRYPTED : KEYMASTER_LABELS.SUCCESS_DECRYPTED);
    } catch (err) {
      onNotify("error", `${KEYMASTER_LABELS.CONVERSION_FAILED}: ${toErrorMessage(err)}`);
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [inputValue, dataType, action, realmLower, onNotify, convertFn, mountedRef]);

  const handleSwap = useCallback((): void => {
    setActionState((prev) => (prev === "ENCRYPT" ? "DECRYPT" : "ENCRYPT"));
    setInputValue(outputValue);
    setOutputValue("");
  }, [outputValue]);

  const inputLabel = action === "ENCRYPT" ? KEYMASTER_LABELS.PLAIN_TEXT : KEYMASTER_LABELS.ENCRYPTED_TEXT;
  const outputLabel = action === "ENCRYPT" ? KEYMASTER_LABELS.ENCRYPTED_TEXT : KEYMASTER_LABELS.PLAIN_TEXT;
  const selectedAction = useMemo(() => ({ value: action, label: action }), [action]);

  return {
    realm,
    setRealm,
    action,
    setAction,
    dataType,
    setDataType,
    inputValue,
    setInputValue,
    outputValue,
    loading,
    realmLower,
    handleConvert,
    handleSwap,
    inputLabel,
    outputLabel,
    selectedAction,
  };
}
